import { useEffect, useRef, useState } from 'react'
import { useAuthContext } from '@contexts/authContext'
import { fontSize, size } from '@styles/theme'
import useYesOrNo from '@hooks/useYesOrNo'
import styled from '@emotion/styled'
import { Dialog, IconButton, Popover } from '@mui/material'
import { PiCopy, PiPlus, PiTrash } from 'react-icons/pi'
import YesOrNo from '@elements/YesOrNo'
import Picker from '@emoji-mart/react'

const ActionContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  height: ${size(6)};
  min-width: ${size(16)};
  padding: 0 ${size(1)} 0 ${size(1)};

  border-radius: 100px;
  background-color: #333333;
  border: 0.5px solid rgba(255,255,255,0.2);
`

const ActionButton = styled(IconButton)`
  display: flex;
  align-items: center;
  justify-content: center;

  height: ${size(4)};
  width: ${size(4)};
  margin-left: ${size(0.5)};

  border-radius: 100%;
`

const EmojiButton = styled.div`
  fontSize: ${fontSize('xl')};
  color: black;
`

const defaultReactions = [
  { emoji: '❤️', action: 'create' },
  { emoji: '🔥', action: 'create' },
  { emoji: '😀', action: 'create' },
  { emoji: '😈', action: 'create' },
  { emoji: '😏', action: 'create' }
]

const MessageActions = ({ open, setOpen, anchorEl, isOwnMessage, destroyFunc, data, destroyDialogue, createMessageReaction, showCopy=true, messageReactions=[], destroyMessageReaction, clipboardContents }) => {
  const { state: { user } } = useAuthContext()
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const [reactionsToRender, setReactionsToRender] = useState(defaultReactions)
  const [reactionHash, setReactionHash] = useState({})
  const [isDestroyDialogOpen, openDestroyDialog, closeDestroyDialog] = useYesOrNo('message-destroy-confirmation')
  const pendingEmoji = useRef({})

  // Calculates the index storing the users reactions for future rendering
  useEffect(() => {
    if (messageReactions && messageReactions?.length > 0) {
      calculateReactionHash(messageReactions)
    }
  }, [messageReactions])

  // Combines the user's reactions with default reactions to render consistently across changes
  useEffect(() => {
    calculateReactionsToRender()
  }, [reactionHash])
  
  function calculateReactionHash(messageReactions) {
    setReactionHash(messageReactions?.reduce((acc, reaction) => {
      if (reaction?.userUuid === user?.uuid) {
        reaction.action = 'destroy'
        acc[reaction.emoji] = reaction
      }
      return acc
    }, {}))
  }

  function calculateReactionsToRender() {
    const baseReactions = defaultReactions.filter((reaction) => !reactionHash[reaction.emoji])
    setReactionsToRender(Object.values(reactionHash).concat(baseReactions).slice(0, 5))
  }

  const addEmojiToPending = (emoji) => {
    pendingEmoji.current = { ...pendingEmoji.current, [emoji]: true}
  }

  const removeEmojiFromPending = (emoji) => {
    delete pendingEmoji.current[emoji]
  }

  // Sets updated message reaction state, and then fires create request
  const innerCreateMessageReaction = (reaction) => {
    if (pendingEmoji.current[reaction.emoji]) return;
    // Add to pending hash to prevent more requests for this emoji
    addEmojiToPending(reaction.emoji)
    // Update local state right away
    setReactionHash({ ...reactionHash, [reaction.emoji]: { ...reaction, action: 'destroy' } })
    // Send of frequest to create and update the pending emoji hash when finished
    createMessageReaction(reaction.emoji)
      .finally(() => removeEmojiFromPending(reaction.emoji))
  }

  // Sets updated message reaction state, and then fires delete request
  const innerDestroyMessageReaction = (reaction) => {
    if (pendingEmoji.current[reaction.emoji]) return;
    // Add to pending hash to prevent more requests for this emoji
    addEmojiToPending(reaction.emoji)
    // Send of frequest to delete and update the pending emoji hash when finished
    destroyMessageReaction(reactionHash[reaction.emoji]?.uuid)
      .finally(() => removeEmojiFromPending(reaction.emoji))
    // // Update local state right away
    const newHash = {...reactionHash}
    delete newHash[reaction.emoji]
    setReactionHash(newHash)
  }

  // Destroys the entire message
  const handleDestroy = (yesOrNo) => {
    if(yesOrNo) {
      destroyFunc(data)
      setOpen(false)
    }
    closeDestroyDialog()
  }

  const renderReactionButtons = () => {
    const createOrDestroy = (action) => action === 'create' ? innerCreateMessageReaction : innerDestroyMessageReaction
    return reactionsToRender.map((reaction, index) => (
      <ActionButton
        key={index}
        style={{ margin: index === 0 && 0, backgroundColor: reaction.action === 'destroy' ? 'rgba(255,255,255,0.3)' : 'transparent' }}
        onClick={() => createOrDestroy(reaction.action)(reaction)}>
        <EmojiButton>{reaction.emoji}</EmojiButton>
      </ActionButton>
    ))
  }

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={() => setOpen(false)}
      anchorOrigin={{
        vertical: 'top',
        horizontal: isOwnMessage ? 'left' : 'right',
      }}
      transformOrigin={{
        vertical: 'bottom',
        horizontal: isOwnMessage ? 'left' : 'right'
      }}
      slotProps={{
        paper: {
          style: {
            backgroundColor: 'transparent',
            borderRadius: 100,
            padding: 0,
            marginTop: size(-0.3),
          }
        }
      }}>
      <ActionContainer>
        {renderReactionButtons()}
        <ActionButton onClick={() => setShowEmojiPicker(true)}>
          <PiPlus
            fill='lightgrey'
            size={size(3.25)}
            style={{ flexShrink: 0 }}
          />
        </ActionButton>
        {showCopy && clipboardContents &&
          <ActionButton onClick={() => navigator?.clipboard?.writeText(clipboardContents)}>
            <PiCopy
              fill='lightgrey'
              size={size(3.25)}
              style={{ flexShrink: 0 }}
            />
          </ActionButton>
        }
        {isOwnMessage &&
          <ActionButton onClick={() => openDestroyDialog()}>
            <PiTrash
              fill='lightgrey'
              size={size(3.25)}
              style={{ flexShrink: 0 }}
            />
          </ActionButton>
        }
      </ActionContainer>
      <YesOrNo
        isOpen={isDestroyDialogOpen}
        setClosed={closeDestroyDialog}
        handleSelect={handleDestroy}
        {...destroyDialogue}
        />
      <Dialog
        sx={{
          '& .MuiDialog-paper': {
            backgroundColor: 'transparent',
            margin: 0,
          }
        }}
        open={showEmojiPicker}
        onClose={() => setShowEmojiPicker(false)}
      >
        <Picker data={
          async () => {
            const response = await fetch('https://cdn.jsdelivr.net/npm/@emoji-mart/data')
            return response.json()
          }}
          onEmojiSelect={e => {
            setShowEmojiPicker(false)
            createMessageReaction(e.native)
          }}
        />
      </Dialog>
    </Popover>
  )
}

export default MessageActions
